import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { IconClock } from '@calendly/ui/components/glyphs/IconClock';
import { IconPencil } from '@calendly/ui/components/glyphs/IconPencil';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useMainContainerSize } from '@calendly/ui/hooks/main-container-size-context';
import classNames from 'classnames';
import React from 'react';
import { useGetUserQuery } from 'common';
import type { EventMembership } from 'features/meetings/types';
import type { BuffersProps } from './useBuffers';
const bufferContainerCss = "b1gzfl7t";
const bufferRowCss = "b1y7fjpc";
const BufferRow = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} className={classNames(bufferRowCss, props.className)} />;
const bufferTimeRangeCss = "bg6uqjh";
const bufferTimeTextCss = "b1b2bkbd";
const editBufferButtonCss = "e2i1nsr";
const EditBufferButton = (props: React.ComponentProps<typeof BareButton>) => {
  const tooltip = I18n.t('publisher.meetings.edit_buffer');
  return <BareButton {...props} tooltip={tooltip} className={classNames(editBufferButtonCss, props.className)} />;
};
const bufferTextContainerCss = "b1kpss47";
const BufferTextContainer = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} className={classNames(bufferTextContainerCss, props.className)} />;
const BufferMembersData = ({
  memberships,
  isMultiHost
}: {
  memberships: EventMembership[];
  isMultiHost: boolean;
}) => {
  const {
    data: userData
  } = useGetUserQuery(undefined);
  const membershipNames = memberships.map(({
    userId,
    name
  }) => {
    if (userId === userData?.id) {
      return `${name} (${I18n.t('publisher.title.you')})`;
    }
    return name;
  });
  if (!isMultiHost) return null;
  if (memberships.length <= 2) {
    return ` (${membershipNames.join(', ').trim()})`;
  }
  return <Tooltip text={HtmlParser(membershipNames.join('<br/>').trim())}>
      <span>{` (${memberships.length} users)`}</span>
    </Tooltip>;
};

/**
 * Renders a buffer time section above or below a meeting card.
 *
 * Displays one row per distinct buffer length, showing the buffer time range
 * and an optional edit button for the current user's buffer. In multi-host
 * events, each row also lists the host names associated with that buffer length
 * (or a tooltip count when there are more than two).
 *
 * @param entries - Buffer entries to render, each representing a distinct buffer length group
 * @param isMultiHost - Whether the event has multiple hosts; controls member name display
 * @param showBufferEdit - Whether to show the edit button (true when the buffer window hasn't passed)
 * @param testId - `data-testid` applied to the buffer text container for testing
 * @param label - Translated label string (e.g. "Before buffer" / "After buffer")
 * @param isBeforeBuffer - Whether this section is the before-meeting buffer (true) or after-meeting buffer (false)
 * @param onEditBuffer - Callback invoked when the edit button is clicked; first arg is the same as `isBeforeBuffer`
 */
export const Buffers = ({
  entries,
  isMultiHost,
  showBufferEdit,
  testId,
  label,
  isBeforeBuffer,
  onEditBuffer
}: BuffersProps) => {
  const {
    isSmallMainContainer
  } = useMainContainerSize();
  return <div className={bufferContainerCss}>
      {entries.map(({
      bufferLength,
      bufferRange,
      memberships,
      userMembership
    }) => <BufferRow key={bufferLength}>
          <div className={bufferTimeRangeCss}>
            {isSmallMainContainer && <UiIcon icon={IconClock} size="small" />}
            <span className={bufferTimeTextCss}>{bufferRange}</span>
          </div>
          <BufferTextContainer data-testid={testId}>
            {label}
            <BufferMembersData memberships={memberships} isMultiHost={isMultiHost} />
            {!!userMembership && showBufferEdit && <EditBufferButton onClick={() => onEditBuffer(isBeforeBuffer, userMembership)}>
              <UiIcon icon={IconPencil} size="small" spacingLeft="8" />
            </EditBufferButton>}
          </BufferTextContainer>
        </BufferRow>)}
    </div>;
};

require("./Buffers.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Buffers.tsx");